import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  LogOut, Mail, Phone, Building2, Clock, FileText,
  MessageCircle, ChevronDown, ChevronUp, Search, Filter,
  Webhook, CheckCircle2, XCircle, AlertTriangle, Send, Download, Image
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString("nl-NL", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
  });
};

const downloadCSV = (data, columns, filename) => {
  const header = columns.map((c) => c.label).join(";");
  const rows = data.map((item) =>
    columns.map((c) => {
      let val = c.getter(item) ?? "";
      val = String(val).replace(/"/g, '""');
      if (val.includes(";") || val.includes('"') || val.includes("\n")) val = `"${val}"`;
      return val;
    }).join(";")
  );
  const bom = "\uFEFF";
  const csv = bom + [header, ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const quoteCols = [
  { label: "Naam", getter: (q) => q.name },
  { label: "Email", getter: (q) => q.email },
  { label: "Telefoon", getter: (q) => q.phone },
  { label: "Bedrijf", getter: (q) => q.company },
  { label: "Dienst", getter: (q) => q.service_type },
  { label: "Frequentie", getter: (q) => q.frequency },
  { label: "Details", getter: (q) => q.details },
  { label: "Datum", getter: (q) => formatDate(q.created_at) },
];

const contactCols = [
  { label: "Naam", getter: (c) => c.name },
  { label: "Email", getter: (c) => c.email },
  { label: "Telefoon", getter: (c) => c.phone },
  { label: "Bericht", getter: (c) => c.message },
  { label: "Datum", getter: (c) => formatDate(c.created_at) },
];

const webhookCols = [
  { label: "Status", getter: (l) => l.status },
  { label: "Naam", getter: (l) => l.name },
  { label: "Email", getter: (l) => l.email },
  { label: "Dienst", getter: (l) => l.service },
  { label: "Bron", getter: (l) => l.source },
  { label: "HTTP Code", getter: (l) => l.status_code },
  { label: "Fout", getter: (l) => l.error },
  { label: "Datum", getter: (l) => formatDate(l.created_at) },
];

// Login Screen
const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      onLogin(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Inloggen mislukt");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <img src="/logo-clean-force.png" alt="Clean-Force" className="h-8 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white" data-testid="admin-login-title">Admin Dashboard</h1>
          <p className="text-[#A1A1AA] text-sm mt-1">Log in om berichten te beheren</p>
        </div>
        <form onSubmit={handleSubmit} className="p-8 rounded-2xl bg-[#121212] border border-white/10" data-testid="admin-login-form">
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm" data-testid="login-error">
              {error}
            </div>
          )}
          <div className="mb-4">
            <label className="block text-white text-sm font-medium mb-2">Email</label>
            <input
              type="email"
              required
              data-testid="admin-email-input"
              className="w-full px-4 py-3 rounded-xl bg-[#0A0A0A] border border-white/10 text-white focus:border-[#4DD0E1] focus:outline-none transition-colors"
              placeholder="admin@clean-force.eu"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="mb-6">
            <label className="block text-white text-sm font-medium mb-2">Wachtwoord</label>
            <input
              type="password"
              required
              data-testid="admin-password-input"
              className="w-full px-4 py-3 rounded-xl bg-[#0A0A0A] border border-white/10 text-white focus:border-[#4DD0E1] focus:outline-none transition-colors"
              placeholder="Wachtwoord"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full py-3 rounded-xl bg-[#4DD0E1] text-black font-bold hover:bg-[#3bc0d1] transition-colors disabled:opacity-50"
          >
            {loading ? "Inloggen..." : "Inloggen"}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

// Data Table Component
const DataCard = ({ item, type, onStatusChange }) => {
  const [expanded, setExpanded] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);

  const statusColors = {
    pending: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
    in_progress: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    completed: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  };
  const statusLabels = { pending: "Openstaand", in_progress: "In behandeling", completed: "Voltooid" };

  const handleStatusChange = async (newStatus) => {
    setChangingStatus(true);
    try {
      await axios.put(`${API}/quote/${item.id}/status`, { status: newStatus }, { withCredentials: true });
      if (onStatusChange) onStatusChange(item.id, newStatus);
    } catch { /* silent */ }
    finally { setChangingStatus(false); }
  };

  return (
    <div
      className="p-4 rounded-xl bg-[#121212] border border-white/10 hover:border-[#4DD0E1]/30 transition-colors"
      data-testid={`${type}-card-${item.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white font-semibold truncate">{item.name}</span>
            {type === "quote" && (
              <span className="px-2 py-0.5 rounded-full bg-[#4DD0E1]/10 text-[#4DD0E1] text-xs font-medium">
                {item.service_type}
              </span>
            )}
            {type === "quote" && item.status && (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${statusColors[item.status] || statusColors.pending}`}>
                {statusLabels[item.status] || item.status}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1 text-sm text-[#A1A1AA]">
            <span className="flex items-center gap-1"><Mail size={12} /> {item.email}</span>
            {item.phone && <span className="flex items-center gap-1"><Phone size={12} /> {item.phone}</span>}
          </div>
          {type === "quote" && item.company && (
            <div className="flex items-center gap-1 mt-1 text-sm text-[#A1A1AA]">
              <Building2 size={12} /> {item.company}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs text-[#A1A1AA] flex items-center gap-1">
            <Clock size={12} /> {formatDate(item.created_at)}
          </span>
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1 rounded-lg hover:bg-white/5 text-[#A1A1AA] hover:text-white transition-colors"
            data-testid={`expand-${item.id}`}
          >
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>
      {expanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="mt-3 pt-3 border-t border-white/5"
        >
          {type === "quote" && (
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div className="text-sm"><span className="text-[#A1A1AA]">Frequentie:</span> <span className="text-white">{item.frequency}</span></div>
              <div className="text-sm"><span className="text-[#A1A1AA]">Dienst:</span> <span className="text-white">{item.service_type}</span></div>
            </div>
          )}
          {type === "quote" && (
            <div className="flex items-center gap-3 mb-3">
              <span className="text-xs text-[#A1A1AA]">Status:</span>
              <select
                value={item.status || "pending"}
                onChange={(e) => handleStatusChange(e.target.value)}
                disabled={changingStatus}
                data-testid={`status-select-${item.id}`}
                className="text-xs px-2 py-1 rounded-lg bg-[#0A0A0A] border border-white/10 text-white focus:border-[#4DD0E1] focus:outline-none"
              >
                <option value="pending">Openstaand</option>
                <option value="in_progress">In behandeling</option>
                <option value="completed">Voltooid (stuurt na-foto email)</option>
              </select>
              {changingStatus && <div className="w-4 h-4 border-2 border-[#4DD0E1] border-t-transparent rounded-full animate-spin" />}
            </div>
          )}
          <p className="text-sm text-[#A1A1AA]">
            {type === "quote" ? item.details || "Geen details" : item.message}
          </p>
          {item.images && item.images.length > 0 && (
            <div className="mt-3">
              <p className="text-xs text-[#A1A1AA] mb-2 flex items-center gap-1"><Image size={12} /> {item.images.length} foto('s)</p>
              <div className="flex flex-wrap gap-2">
                {item.images.map((imgUrl, i) => (
                  <a key={i} href={`${API.replace('/api', '')}${imgUrl}`} target="_blank" rel="noopener noreferrer" className="block">
                    <img src={`${API.replace('/api', '')}${imgUrl}`} alt="" className="w-20 h-20 object-cover rounded-lg border border-white/10 hover:border-[#4DD0E1]/50 transition-colors" />
                  </a>
                ))}
              </div>
            </div>
          )}
          {type === "quote" && (item.before_images?.length > 0 || item.after_images?.length > 0) && (
            <div className="mt-3 grid grid-cols-2 gap-4">
              {item.before_images?.length > 0 && (
                <div>
                  <p className="text-xs text-[#A1A1AA] mb-2">Voor</p>
                  <div className="flex flex-wrap gap-1">
                    {item.before_images.map((img, i) => (
                      <a key={i} href={`${API.replace('/api', '')}${img}`} target="_blank" rel="noopener noreferrer">
                        <img src={`${API.replace('/api', '')}${img}`} alt="Voor" className="w-16 h-16 object-cover rounded-lg border border-white/10" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {item.after_images?.length > 0 && (
                <div>
                  <p className="text-xs text-emerald-400 mb-2">Na</p>
                  <div className="flex flex-wrap gap-1">
                    {item.after_images.map((img, i) => (
                      <a key={i} href={`${API.replace('/api', '')}${img}`} target="_blank" rel="noopener noreferrer">
                        <img src={`${API.replace('/api', '')}${img}`} alt="Na" className="w-16 h-16 object-cover rounded-lg border border-emerald-500/30" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

// Dashboard
const Dashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState("quotes");
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sendingSummary, setSendingSummary] = useState(false);
  const [summaryMsg, setSummaryMsg] = useState("");
  const onLogoutRef = useRef(onLogout);
  onLogoutRef.current = onLogout;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [qRes, cRes, wRes] = await Promise.all([
          axios.get(`${API}/quote`, { withCredentials: true }),
          axios.get(`${API}/contact`, { withCredentials: true }),
          axios.get(`${API}/webhook-logs`, { withCredentials: true }),
        ]);
        setQuotes(qRes.data);
        setContacts(cRes.data);
        setWebhookLogs(wRes.data);
      } catch (err) {
        if (err.response?.status === 401) onLogoutRef.current();
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch { /* ignore */ }
    onLogoutRef.current();
  };

  const handleSendSummary = async () => {
    setSendingSummary(true);
    setSummaryMsg("");
    try {
      const { data } = await axios.post(`${API}/webhook-logs/send-summary`, {}, { withCredentials: true });
      setSummaryMsg(data.message);
    } catch {
      setSummaryMsg("Verzenden mislukt");
    } finally {
      setSendingSummary(false);
      setTimeout(() => setSummaryMsg(""), 4000);
    }
  };

  const filteredData = (activeTab === "quotes" ? quotes : activeTab === "contacts" ? contacts : [])
    .filter((item) => {
      if (!search) return true;
      const s = search.toLowerCase();
      return (
        item.name?.toLowerCase().includes(s) ||
        item.email?.toLowerCase().includes(s) ||
        item.phone?.toLowerCase().includes(s) ||
        (item.service_type?.toLowerCase().includes(s)) ||
        (item.message?.toLowerCase().includes(s)) ||
        (item.details?.toLowerCase().includes(s))
      );
    })
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const filteredWebhookLogs = webhookLogs.filter((log) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      log.name?.toLowerCase().includes(s) ||
      log.email?.toLowerCase().includes(s) ||
      log.service?.toLowerCase().includes(s) ||
      log.source?.toLowerCase().includes(s) ||
      log.status?.toLowerCase().includes(s)
    );
  });

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-[#121212] border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/logo-clean-force.png" alt="Clean-Force" className="h-6" />
            <span className="text-[#A1A1AA] text-sm">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#A1A1AA]">{user.email}</span>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 text-[#A1A1AA] hover:text-white hover:bg-white/10 transition-colors text-sm"
            >
              <LogOut size={16} /> Uitloggen
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {[
            { label: "Offerte-aanvragen", value: quotes.length, icon: FileText, color: "#4DD0E1" },
            { label: "Contactberichten", value: contacts.length, icon: MessageCircle, color: "#FBBC04" },
            { label: "Deze maand", value: quotes.filter(q => new Date(q.created_at).getMonth() === new Date().getMonth()).length, icon: Clock, color: "#34A853" },
            { label: "Via chatbot", value: quotes.filter(q => q.details?.includes("[Via Chatbot]")).length, icon: MessageCircle, color: "#EA4335" },
            { label: "Webhooks", value: webhookLogs.length, icon: Webhook, color: "#A78BFA",
              sub: webhookLogs.filter(l => l.status === "success").length + " OK" },
          ].map((stat) => (
            <div key={stat.label} className="p-4 rounded-xl bg-[#121212] border border-white/10" data-testid={`stat-card-${stat.label}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${stat.color}15` }}>
                  <stat.icon size={20} style={{ color: stat.color }} />
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">{loading ? "-" : stat.value}</div>
                  <div className="text-xs text-[#A1A1AA]">{stat.label}</div>
                  {stat.sub && <div className="text-xs text-emerald-400">{stat.sub}</div>}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs + Search */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab("quotes")}
              data-testid="tab-quotes"
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                activeTab === "quotes" ? "bg-[#4DD0E1] text-black" : "bg-white/5 text-[#A1A1AA] hover:text-white"
              }`}
            >
              <FileText size={14} className="inline mr-2" />
              Offertes ({quotes.length})
            </button>
            <button
              onClick={() => setActiveTab("contacts")}
              data-testid="tab-contacts"
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                activeTab === "contacts" ? "bg-[#4DD0E1] text-black" : "bg-white/5 text-[#A1A1AA] hover:text-white"
              }`}
            >
              <MessageCircle size={14} className="inline mr-2" />
              Berichten ({contacts.length})
            </button>
            <button
              onClick={() => setActiveTab("webhooks")}
              data-testid="tab-webhooks"
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                activeTab === "webhooks" ? "bg-[#A78BFA] text-black" : "bg-white/5 text-[#A1A1AA] hover:text-white"
              }`}
            >
              <Webhook size={14} className="inline mr-2" />
              Webhook Logs ({webhookLogs.length})
            </button>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-72">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
              <input
                type="text"
                data-testid="admin-search"
                placeholder="Zoeken..."
                className="w-full pl-10 pr-4 py-2 rounded-xl bg-[#121212] border border-white/10 text-white text-sm focus:border-[#4DD0E1] focus:outline-none transition-colors"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {activeTab === "quotes" && (
              <button
                onClick={() => downloadCSV(filteredData, quoteCols, `offertes_${new Date().toISOString().slice(0,10)}.csv`)}
                data-testid="download-quotes-csv"
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#4DD0E1]/10 text-[#4DD0E1] hover:bg-[#4DD0E1]/20 transition-colors text-sm font-medium whitespace-nowrap"
              >
                <Download size={14} /> CSV
              </button>
            )}
            {activeTab === "contacts" && (
              <button
                onClick={() => downloadCSV(filteredData, contactCols, `berichten_${new Date().toISOString().slice(0,10)}.csv`)}
                data-testid="download-contacts-csv"
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#FBBC04]/10 text-[#FBBC04] hover:bg-[#FBBC04]/20 transition-colors text-sm font-medium whitespace-nowrap"
              >
                <Download size={14} /> CSV
              </button>
            )}
            {activeTab === "webhooks" && (
              <button
                onClick={() => downloadCSV(filteredWebhookLogs, webhookCols, `webhook_logs_${new Date().toISOString().slice(0,10)}.csv`)}
                data-testid="download-webhooks-csv"
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#A78BFA]/10 text-[#A78BFA] hover:bg-[#A78BFA]/20 transition-colors text-sm font-medium whitespace-nowrap"
              >
                <Download size={14} /> CSV
              </button>
            )}
          </div>
        </div>

        {/* Data */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-[#4DD0E1] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : activeTab === "webhooks" ? (
          <>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-[#A1A1AA]">
                Dagelijkse samenvatting wordt automatisch om 08:00 verzonden naar office@clean-force.eu
              </p>
              <div className="flex items-center gap-3">
                {summaryMsg && <span className="text-sm text-emerald-400">{summaryMsg}</span>}
                <button
                  onClick={handleSendSummary}
                  disabled={sendingSummary}
                  data-testid="send-summary-btn"
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#A78BFA]/10 text-[#A78BFA] hover:bg-[#A78BFA]/20 transition-colors text-sm font-medium disabled:opacity-50"
                >
                  <Send size={14} /> {sendingSummary ? "Verzenden..." : "Samenvatting nu versturen"}
                </button>
              </div>
            </div>
            {filteredWebhookLogs.length === 0 ? (
            <div className="text-center py-16 text-[#A1A1AA]" data-testid="no-webhook-data">
              <Webhook size={40} className="mx-auto mb-4 opacity-50" />
              <p>Geen webhook logs gevonden</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-white/10" data-testid="webhook-logs-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#121212] text-left text-[#A1A1AA]">
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Naam</th>
                    <th className="px-4 py-3 font-medium">Email</th>
                    <th className="px-4 py-3 font-medium">Dienst</th>
                    <th className="px-4 py-3 font-medium">Bron</th>
                    <th className="px-4 py-3 font-medium">Code</th>
                    <th className="px-4 py-3 font-medium">Tijdstip</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredWebhookLogs.map((log) => (
                    <tr key={log.id} className="border-t border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`webhook-log-${log.id}`}>
                      <td className="px-4 py-3">
                        {log.status === "success" ? (
                          <span className="inline-flex items-center gap-1 text-emerald-400"><CheckCircle2 size={14} /> OK</span>
                        ) : log.status === "error" ? (
                          <span className="inline-flex items-center gap-1 text-red-400"><XCircle size={14} /> Error</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-amber-400"><AlertTriangle size={14} /> {log.status_code || "?"}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-white">{log.name || "-"}</td>
                      <td className="px-4 py-3 text-[#A1A1AA]">{log.email || "-"}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 rounded-full bg-[#4DD0E1]/10 text-[#4DD0E1] text-xs">{log.service || "-"}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          log.source === "chatbot" ? "bg-purple-500/10 text-purple-400" :
                          log.source === "contactformulier" ? "bg-yellow-500/10 text-yellow-400" :
                          "bg-cyan-500/10 text-cyan-400"
                        }`}>{log.source}</span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-[#A1A1AA]">{log.status_code ?? "-"}</td>
                      <td className="px-4 py-3 text-xs text-[#A1A1AA]">{formatDate(log.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          </>
        ) : filteredData.length === 0 ? (
          <div className="text-center py-16 text-[#A1A1AA]" data-testid="no-data-message">
            <Filter size={40} className="mx-auto mb-4 opacity-50" />
            <p>Geen {activeTab === "quotes" ? "offertes" : "berichten"} gevonden</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="data-list">
            {filteredData.map((item) => (
              <DataCard key={item.id} item={item} type={activeTab === "quotes" ? "quote" : "contact"}
                onStatusChange={(id, status) => {
                  setQuotes(prev => prev.map(q => q.id === id ? { ...q, status } : q));
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Main Admin Page
export default function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(data);
      } catch {
        // Not authenticated - cookie missing or expired
      } finally {
        setChecking(false);
      }
    };
    checkAuth();
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#4DD0E1] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <AdminLogin onLogin={(data) => setUser(data)} />;
  }

  return <Dashboard user={user} onLogout={() => { setUser(null); navigate("/admin"); }} />;
}
